import { useNavigate, useSearch } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card';
import { SetPasswordForm } from '~/features/auth/components/set-password-form';
import { useInviteStatus } from '~/features/auth/hooks/use-invite-status';

/**
 * Token-based set-password page reached after email verification. Reads the
 * user_id + token from the link, then gates on a non-consuming invite-status
 * check: already-set-up users are sent to sign-in, invalid/expired links bounce
 * to /login, and only a still-valid link renders the form.
 */
export function ResetPasswordPage() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const { user_id: userId, token } = useSearch({ from: '/reset-password' });

  const { data: status, isLoading, isError } = useInviteStatus(userId, token);

  // Redirect to /login with a reason once we know the link can't be used:
  // missing identifiers, an already-set-up account, or an invalid/expired link.
  useEffect(() => {
    if (!userId || !token) {
      void navigate({
        to: '/login',
        search: { error: t('setPassword.invalidLink') },
        replace: true,
      });
      return;
    }
    if (status?.alreadySetUp) {
      void navigate({
        to: '/login',
        search: { error: t('setPassword.alreadySetUp') },
        replace: true,
      });
      return;
    }
    if (isError || (status && !status.valid)) {
      void navigate({
        to: '/login',
        search: { error: t('setPassword.invalidLink') },
        replace: true,
      });
    }
  }, [userId, token, status, isError, navigate, t]);

  // Spinner while we resolve link status (or while a redirect is in flight) so
  // the form never flashes for an already-set-up / invalid link.
  if (!userId || !token || isLoading || isError || !status?.valid || status.alreadySetUp) {
    return (
      <div className="bg-gradient-page flex min-h-dvh items-center justify-center">
        <Loader2 className="text-primary h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-gradient-page flex min-h-dvh items-center justify-center p-6">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-lg">{t('setPassword.title')}</CardTitle>
          <CardDescription>{t('setPassword.subtitle')}</CardDescription>
        </CardHeader>
        <CardContent>
          <SetPasswordForm userId={userId} token={token} />
        </CardContent>
      </Card>
    </div>
  );
}
